"use client";

import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";
import { NavbarItem } from "./navbar";
import { useIsApple, useSearch } from "./SearchProvider";
import { SidebarItem, SidebarLabel } from "./sidebar";
import { Code, Text } from "./text";

export default function Search({
  searchLinks,
}: {
  searchLinks: Promise<{ id: number; name: string; url: string; tags: string[] }[]> | { id: number; name: string; url: string; tags: string[] }[];
}) {
  const [links, setLinks] = useState<{ id: number; name: string; url: string; tags: string[] }[]>([]);
  
  useEffect(() => {
    if (searchLinks instanceof Promise) {
      searchLinks.then(setLinks).catch(console.error);
    } else {
      setLinks(searchLinks);
    }
  }, [searchLinks]);
  const [query, setQuery] = useState("");
  const { isSearchOpen, setSearchOpen } = useSearch();

  const filteredQuickLinks = useMemo(() => {
    const fuse = new Fuse(links, { keys: ["name", "url", "tags"] });
    return fuse.search(query);
  }, [links, query]);

  return (
    <Dialog
      className={"relative z-10"}
      open={isSearchOpen}
      onClose={() => {
        setSearchOpen(false);
        setQuery("");
      }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-950/25 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className="mx-auto max-w-2xl transform divide-y divide-zinc-500/10 overflow-hidden rounded-xl bg-white/80 ring-1 shadow-2xl ring-black/5 backdrop-blur-sm backdrop-filter transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in dark:bg-black/80 dark:ring-white/5"
        >
          <Combobox
            onChange={(item: (typeof links)[number] | null) => {
              if (item) window.open(item.url, "_blank");
            }}
          >
            <div className="grid grid-cols-1">
              <ComboboxInput
                autoFocus
                className="col-start-1 row-start-1 h-12 w-full bg-transparent pr-4 pl-11 text-base outline-hidden placeholder:text-zinc-500 sm:text-sm dark:text-white dark:caret-white"
                placeholder="Search..."
                onChange={(event) => setQuery(event.target.value)}
                onBlur={() => setQuery("")}
              />
              <MagnifyingGlassIcon
                className="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-zinc-900/40 dark:text-zinc-100/40"
                aria-hidden="true"
              />
            </div>

            {(query !== "" || filteredQuickLinks.length > 0) && (
              <ComboboxOptions
                static
                as="ul"
                className="max-h-80 scroll-py-2 divide-y divide-zinc-500/10 overflow-y-auto"
              >
                <li className="p-2">
                  <ul className="text-sm text-zinc-700 dark:text-zinc-300">
                    {filteredQuickLinks.map(({ item: project }: { item: { id: number; name: string; url: string; tags: string[] } }) => (
                      <ComboboxOption
                        as="li"
                        key={project.id}
                        value={project}
                        className="group flex cursor-default items-center rounded-md px-3 py-2 select-none data-focus:bg-zinc-900/5 data-focus:text-zinc-900 data-focus:outline-hidden dark:data-focus:bg-zinc-100/5 dark:data-focus:text-zinc-100"
                      >
                        <ArrowTopRightOnSquareIcon
                          className="size-6 flex-none text-zinc-900/40 group-data-focus:text-zinc-900 dark:text-zinc-100/40 dark:group-data-focus:text-zinc-100"
                          aria-hidden="true"
                        />
                        <span className="ml-3 flex-auto truncate">{project.name}</span>
                        <span className="ml-3 hidden flex-none text-zinc-500 group-data-focus:inline">Jump to...</span>
                      </ComboboxOption>
                    ))}
                  </ul>
                </li>
              </ComboboxOptions>
            )}

            {query !== "" && filteredQuickLinks.length === 0 && (
              <div className="px-6 py-14 text-center sm:px-14">
                <ArrowTopRightOnSquareIcon
                  className="mx-auto size-6 text-zinc-900/40 dark:text-zinc-100/40"
                  aria-hidden="true"
                />
                <p className="mt-4 text-sm text-zinc-900 dark:text-zinc-100">
                  We couldn&apos;t find any quick links with that term. Please try again.
                </p>
              </div>
            )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export function SearchSidebarItem() {
  const { setSearchOpen } = useSearch();
  return (
    <SidebarItem onClick={() => setSearchOpen(true)}>
      <SidebarLabel>Suche</SidebarLabel>
    </SidebarItem>
  );
}

export function SearchNavbarItem() {
  const { setSearchOpen } = useSearch();
  return (
    <NavbarItem onClick={() => setSearchOpen(true)}>
      Suche
    </NavbarItem>
  );
}
